/**
  * @class meleUploader
  * @description
  *
  * # Purpose
  * Implements `<mele-uploader>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-uploader></mele-uploader>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-uploader',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleView from 'meleView'
const mcrypt = require('msysCrypt')

const PROPERTIES = _.extend({}, {
  mime: { type: 'array', description: 'allowed mime types', default: ['image/*'] },
  height: { type: 'integer', description: 'height of element', default: '300' },
  items: { type: 'array', description: 'list of uploaded files' },
  multi: { type: 'boolean', description: 'allows multifile to be uploaded', default: true },
  readonly: { type: 'boolean', description: 'removes footerbuttons' },
  rendertype: { type: 'string', description: 'Type of uploader display', default: 'grid' },
  uploadlimit: { type: 'integer', description: "limit's max total size of all uploaded items in Mb", default: 10 },
  eventFilesChanged: { type: 'function', description: 'method that can be overwritten to inform about change of uploaded items' }, 
}, meleView._properties)

export default class meleUploader extends meleView {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()

    this._elFileInput = this._footer_wrapper.appendChildren({
      tag: 'input',
      type: 'file',
      accept: this.mime.join(','),
      capture: this.mime.some(m => /image|video|jpg|png|gif|tiff|jpeg/.test(m)) ? 'environment' : null,
      multiple: true,
      onchange: (e)=>{
        this.setItems(this._prepareFileItems(e.target.files), true)
      },
    })

    this.readonlyChanged(this.readonly)
  } // init

  multiChanged(value) {}

  mimeChanged(value) {}

  heightChanged(value) {
    this.style.setProperty('--uploader-body-height', value + 'px')
  }

  rendertypeChanged(value, old) { 
    if(this.elUploader) this.elUploader.rendertype = value || PROPERTIES.rendertype?.default ;
  } // rendertypeChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "readonly"
  */
  readonlyChanged(value) {
    if(value) {
      this.footerbuttons = []
    } else {
      this.footerbuttons = [
        { tag: 'mele-button', key: 'clear', label: 'form.clear', color: 'red', iconright: 'office.clear', hidden: true, eventButtonClicked: this._handleClear.bind(this) },
        { tag: 'mele-button', key: 'upload', label: 'form.upload', iconright: 'office.attachment', eventButtonClicked: this._handleInsert.bind(this) },
      ]
    }
  } // readonlyChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "uploadlimit"
  */
  uploadlimitChanged(value) {
    // @TODO implement this
  } // uploadlimitChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Coerces item for set and get methods
  */
  coerceItem(json) {
    json = _.extend({
      tag: 'mele-file',
      readonly: this.readonly,
    }, json)
    return json
  } // coerceItem

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "items"
  */
  itemsChanged(value, old, context_user_interaction) {
    this.eventFilesChanged(this, value, old, context_user_interaction)
  } //itemsChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Updates items in DOM
  */
  updateItems(items, context_user_interaction) {
    super.updateItems(items)
    if(context_user_interaction) { this.itemsChanged(this.items, this.items, true) }
  } // updateItems

  setItems(items, context_user_interaction) {
    super.setItems(items)
    if(context_user_interaction) { this.itemsChanged(this.items, this.items, true) }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Removes items in DOM
  */
  removeItems(items, context_user_interaction) {
    this.deleteItems(items)
    this.itemsChanged(this.items, this.items, context_user_interaction)
  } // removeItems

  _handleClear() {
    this.removeItems(null, true)
  } // _handleClear

  _handleInsert() {
    this._elFileInput.click()
  } // _handleInsert

  formStatusChanged() {
    this.unavailable = !this.items || this.items.length < 1
  } // formStatusChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Maps data into an usable mele-file element
  */
  _prepareFileItems(files) {
    if(files.length < 1) return

    const fl = files.length
    let i = 0
    const items = []
    while (i < fl) {
      const file = files[i]
      const blob = URL.createObjectURL(file)
      items.push(_.extend({
        key: mcrypt.md5(blob),
        name: file.name,
        mime: file.type,
        size: file.size,
        timestamp: parseInt(file.lastModified),
        blob,
      }, this._meta))
      delete this._meta
      i++
    }
    return items
  } // _prepareFiles

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates Blobs from Base64 string
  */
  blobFromBase64(string) {
    const files = string.split('∫')
    const items = files.map(dataurl=>this._dataURLtoBlob(dataurl)).filter(blob=>!!blob)
    this.setItems(this._prepareFileItems(items), false)
  } // blobFromBase64

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates Base64 files from Blob
  */
  async base64FromBlob(files) {
    const results = []

    for (const file of files) {
      try {
      // Fetch Blob from browser
        const response = await fetch(file.blob)
        const blob = await response.blob()

        // Convert blob to base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })

        // Convert blob into base64 and store meta data into it
        results.push(this._saveMetadata(file, base64))
      } catch (error) {
        console.error('Error converting file to base64:', error)
      // Optionally continue with other files or throw
      }
    }

    return results.join('∫')
  } // base64FromBlob

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Insert encoded file metadata into Base64
  */
  _saveMetadata(source, target) {
    const m = target.split(',')
    if(!m) return target
    const encoded = btoa(JSON.stringify(_.pick(source, ['key', 'name', 'create'])))
    return m[0] + ';' + encoded + ',' +m[1]
  } // _saveMetadata

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Conversion of Base64 into Blob
  */
  _dataURLtoBlob(dataurl) {
    try {
      if (!dataurl || typeof dataurl !== 'string') {
        return null
      }

      const m = dataurl.split(',')
      if (m.length < 2) {
        console.error('Invalid data URL format - no comma separator')
        return null
      }

      const mimeMatch = m[0].match(/:(.*?);/)
      if (!mimeMatch || !mimeMatch[1]) {
        console.error('Invalid data URL format - no mime type')
        return null
      }

      const mime = mimeMatch[1]
      const meta = m[0].split(';')
      const bstr = atob(decodeURIComponent(m[1]))

      const n = bstr.length
      const u8arr = new Uint8Array(n)
      for (let i = 0; i < n; i++) {
        u8arr[i] = bstr.charCodeAt(i)
      }

      // Check if meta[2] exists and is valid
      if (meta.length > 2 && /^[A-Za-z0-9+/=]+$/.test(meta[2])) {
        try {
          this._meta = JSON.parse(atob(meta[2]))
        } catch (e) {
          console.error('Error in parsing metadata from meta[2]:', e)
        }
      } else {
      // Attempt to find metadata in meta[0] or meta[1]
        let potentialMeta = null

        // Check for various image types (JPEG, PNG, etc.)
        if (/data:image\/(jpeg|png)/.test(meta[0])) {
        // Example regex to extract metadata that might be embedded elsewhere
          const match = meta[0].match(/;([^;]+);base64/)
          if (match && match[1]) { // ← ADD THIS CHECK
            try {
              potentialMeta = JSON.parse(atob(match[1]))
              this._meta = potentialMeta
            } catch (e) {
              console.error('Error parsing potential metadata:', e)
            }
          }
        // If no metadata found, that's OK - just continue without it
        }
      }

      return new Blob([u8arr], { type: mime })
    } catch (e) {
      console.error('Error converting Base64 to Blob:', e)
      return null // ← Return null instead of undefined
    }
  }
  // _dataURLtoBlob

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Used to inform other elements of files change
  */
  eventFilesChanged(self, items, old, context_user_interaction) {
  } // eventFilesChanged
} // class meleUploader

meleUploader._properties = PROPERTIES
meleUploader.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-uploader', meleUploader)
